import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  forwardRef,
  Ref,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  FormControl,
  Typography,
  Theme,
  SxProps,
  Slide,
  Stack,
  FilledTextFieldProps as MUITextFieldProps,
  IconButton,
  TextField,
} from '@mui/material';
import {IoMdArrowDropdown, IoMdArrowDropup} from 'react-icons/io';

export interface FlatNumericInputProps
  extends Omit<MUITextFieldProps, 'variant' | 'type' | 'onChange'> {
  label?: string;
  errors?: string;
  readonly?: boolean;
  sx?: SxProps<Theme>;
  min?: number;
  max?: number;
  step?: number;
  counter?: {current: number; max: number};
  value?: string | number;
  onChange?: (event: ChangeEvent<HTMLInputElement>, value?: number) => void;
}

export const FlatNumericInput = forwardRef(
  (
    {
      label,
      errors,
      required,
      counter,
      disabled,
      readonly,
      onChange,
      onBlur,
      onKeyDown,
      step = 1,
      min,
      max,
      sx,
      value,
      ...rest
    }: FlatNumericInputProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const id = useId();
    const [inputValue, setInputValue] = useState(() => formatNumberValue(value));
    const allowDecimals = useMemo(() => step % 1 !== 0, [step]);
    const decimalPrecision = useMemo(() => getDecimalPrecision(step), [step]);
    const allowNegative = useMemo(() => typeof min !== 'number' || min < 0, [min]);
    const uniqueId = rest.id ?? `input_${id}`;
    const hasError = !!errors;

    useEffect(() => {
      const nextValue = formatNumberValue(value);

      setInputValue((currentValue: string) => {
        if (nextValue === currentValue) return currentValue;

        if (
          isTransientDecimalValue(currentValue) &&
          nextValue !== '' &&
          Number.parseFloat(currentValue) === Number(nextValue)
        ) {
          return currentValue;
        }

        return nextValue;
      });
    }, [value]);

    const emitChange = (event: ChangeEvent<HTMLInputElement>, nextValue: string) => {
      if (!onChange) return;

      if (nextValue === '' || nextValue === '-' || nextValue === '.' || nextValue === '-.') {
        onChange(event, undefined);
        return;
      }

      const parsedValue = Number.parseFloat(nextValue);

      if (!Number.isNaN(parsedValue)) {
        onChange(event, parsedValue);
      }
    };

    const getInputPattern = () => {
      if (allowDecimals) {
        return allowNegative ? /^-?\d*(?:\.\d*)?$/ : /^\d*(?:\.\d*)?$/;
      }

      return allowNegative ? /^-?\d*$/ : /^\d*$/;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = stripLeadingZeros(event.target.value.replace(/,/g, '.'), allowDecimals);

      if (!getInputPattern().test(nextValue)) {
        return;
      }

      setInputValue(nextValue);
      emitChange(event, nextValue);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      const normalizedValue = normalizeValue(
        inputValue,
        formatNumberValue(value),
        min,
        max,
        allowDecimals
      );

      if (normalizedValue !== inputValue) {
        setInputValue(normalizedValue);
      }

      if (normalizedValue !== inputValue) {
        emitChange(event as ChangeEvent<HTMLInputElement>, normalizedValue);
      }

      onBlur?.(event);
    };

    const updateByStep = (direction: 1 | -1) => {
      const baseValue = inputValue === '' ? 0 : Number.parseFloat(inputValue);
      const safeBaseValue = Number.isNaN(baseValue) ? 0 : baseValue;
      const stepPrecision = Math.max(decimalPrecision, getDecimalPrecision(inputValue));
      const nextValue = clampValue(safeBaseValue + direction * step, min, max);
      const formattedValue = allowDecimals
        ? `${Number(nextValue.toFixed(stepPrecision))}`
        : `${Math.trunc(nextValue)}`;

      setInputValue(formattedValue);

      const syntheticEvent = {
        target: {value: formattedValue},
      } as ChangeEvent<HTMLInputElement>;

      emitChange(syntheticEvent, formattedValue);
    };

    const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || disabled || readonly) {
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        updateByStep(1);
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        updateByStep(-1);
      }
    };

    return (
      <FormControl
        className={`flat-input ${disabled ? 'disabled' : ''}`}
        sx={{
          ...sx,
          width: 'auto',
        }}
      >
        {label && (
          <Box className="label-container">
            <Typography component="label" htmlFor={uniqueId} noWrap>
              {label}
            </Typography>
            {required && !disabled && <Typography component="span">*</Typography>}
          </Box>
        )}

        <Stack flexDirection="row" sx={{width: '100%'}}>
          <TextField
            id={uniqueId}
            ref={ref}
            autoComplete="off"
            {...rest}
            value={inputValue}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyPress}
            variant="filled"
            error={hasError}
            type="text"
            sx={{
              width: '100%',
              '.MuiFilledInput-root': {
                borderRadius: '5px 5px 0px 0px',
              },
              // '.MuiInputBase-root': {
              //   padding: '8.5px 12px',
              // },
              '.MuiFilledInput-input': {
                padding: 0,
                textAlign: 'center',
              },
              ...sx,
            }}
            inputProps={{
              ...rest.inputProps,
              inputMode: allowDecimals ? 'decimal' : 'numeric',
              readOnly: readonly,
            }}
            InputProps={{
              ...rest.InputProps,
              readOnly: disabled || readonly || rest.InputProps?.readOnly,
            }}
          />
          <Stack
            sx={{
              justifyContent: 'center',
              ml: 0.5,
            }}
          >
            <IconButton
              size="small"
              disabled={disabled || readonly}
              style={{width: '20px', height: '20px', padding: '0'}}
              onClick={() => updateByStep(1)}
            >
              <IoMdArrowDropup fontSize="16px" />
            </IconButton>
            <IconButton
              size="small"
              disabled={disabled || readonly}
              style={{width: '20px', height: '20px', padding: '0'}}
              onClick={() => updateByStep(-1)}
            >
              <IoMdArrowDropdown fontSize="16px" />
            </IconButton>
          </Stack>
        </Stack>

        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: 'var(--dcm-red-base)'}}>
            {errors}
          </span>
        </Slide>

        {counter && (
          <span
            className="max-counter"
            style={{
              color: counter.current <= counter.max ? 'var(--dcm-gray)' : 'var(--dcm-red-base)',
            }}
          >
            {counter.current}/{counter.max}
          </span>
        )}
      </FormControl>
    );
  }
);

// -----------------------------------------------------------------
// --------------------------- UTILS -------------------------------
// -----------------------------------------------------------------
const getDecimalPrecision = (value?: string | number) => {
  if (typeof value === 'number' && !Number.isFinite(value)) return 0;
  if (value === undefined || value === null || value === '') return 0;

  const normalized = `${value}`.toLowerCase();

  if (normalized.includes('e-')) {
    const [, exponent] = normalized.split('e-');
    return Number(exponent);
  }

  const decimals = normalized.split('.')[1];
  return decimals ? decimals.length : 0;
};

const formatNumberValue = (value?: string | number | readonly string[]) => {
  if (Array.isArray(value)) return value[0] ?? '';
  if (value === undefined || value === null) return '';
  if (typeof value === 'number' && !Number.isFinite(value)) return '';
  return `${value}`;
};

const clampValue = (value: number, min?: number, max?: number) => {
  let nextValue = value;

  if (typeof min === 'number') {
    nextValue = Math.max(nextValue, min);
  }

  if (typeof max === 'number') {
    nextValue = Math.min(nextValue, max);
  }

  return nextValue;
};

const normalizeValue = (
  rawValue: string,
  fallbackValue: string,
  min?: number,
  max?: number,
  allowDecimals?: boolean
) => {
  if (rawValue === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.') {
    if (typeof min === 'number') {
      if (allowDecimals) {
        return formatNumberValue(min);
      }

      return `${Math.trunc(min)}`;
    }

    return '';
  }

  const parsedValue = Number.parseFloat(rawValue);

  if (Number.isNaN(parsedValue)) {
    return fallbackValue;
  }

  const clampedValue = clampValue(parsedValue, min, max);

  if (allowDecimals) {
    return formatNumberValue(clampedValue);
  }

  return `${Math.trunc(clampedValue)}`;
};

const stripLeadingZeros = (rawValue: string, allowDecimals?: boolean) => {
  if (rawValue === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.') {
    return rawValue;
  }

  const isNegative = rawValue.startsWith('-');
  const unsignedValue = isNegative ? rawValue.slice(1) : rawValue;

  if (allowDecimals && unsignedValue.includes('.')) {
    const [integerPart, fractionalPart = ''] = unsignedValue.split('.');
    const trimmedIntegerPart = integerPart.replace(/^0+(?=\d)/, '') || '0';
    const normalizedValue = `${trimmedIntegerPart}.${fractionalPart}`;

    return isNegative ? `-${normalizedValue}` : normalizedValue;
  }

  const trimmedValue = unsignedValue.replace(/^0+(?=\d)/, '');
  const normalizedValue = trimmedValue === '' ? '0' : trimmedValue;

  return isNegative ? `-${normalizedValue}` : normalizedValue;
};

const isTransientDecimalValue = (value: string) => {
  return value.endsWith('.');
};
